import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "./AuthProvider";
import { skuApi, templateApi, brandingApi, apiError } from "./api/lab";

// App-wide lab state: toast notifications and a cache of the lab's active
// catalogue (used by the booking / invoice forms). Everything else is fetched
// by the screen that needs it.

const AppContext = createContext();
export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const { user } = useAuth();
  const labId = user?.labId ?? null;

  // ── Toasts ────────────────────────────────────────────────────────────────
  const [toasts, setToasts] = useState([]);
  const seq = useRef(0);
  const push = useCallback((kind, text) => {
    const id = ++seq.current;
    setToasts((t) => [...t, { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === "error" ? 6000 : 3500);
  }, []);
  const toast = useMemo(
    () => ({
      success: (t) => push("success", t),
      error: (t) => push("error", typeof t === "string" ? t : apiError(t)),
      info: (t) => push("info", t),
    }),
    [push]
  );

  // ── Catalogue cache (active SKUs of the current lab) ──────────────────────
  const [skus, setSkus] = useState([]);
  const [skusLoaded, setSkusLoaded] = useState(false);
  const loadSkus = useCallback(async (force = false) => {
    if (!labId) return [];
    if (skusLoaded && !force) return skus;
    try {
      const res = await skuApi.list({ isActive: true, limit: 200 });
      const data = res?.data ?? [];
      setSkus(data);
      setSkusLoaded(true);
      return data;
    } catch (e) {
      toast.error(e);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labId, skusLoaded, skus]);

  // ── Test definitions cache (id → { name, shortName, category }) ───────────
  const [templates, setTemplates] = useState({});
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const loadTemplates = useCallback(async (force = false) => {
    if (!labId) return {};
    if (templatesLoaded && !force) return templates;
    try {
      const res = await templateApi.list();
      const map = {};
      for (const t of res?.data ?? []) map[t._id] = t;
      setTemplates(map);
      setTemplatesLoaded(true);
      return map;
    } catch (e) {
      toast.error(e);
      return {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labId, templatesLoaded, templates]);

  // ── Branding (print layout default, GSTIN hint) ───────────────────────────
  const [branding, setBranding] = useState(null);   // { labId, name, contact, branding }
  const loadBranding = useCallback(async (force = false) => {
    if (!labId) return null;
    if (branding && !force) return branding;
    try {
      const b = await brandingApi.get();
      setBranding(b);
      return b;
    } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labId, branding]);
  const defaultLayout = branding?.branding?.layout || "branded";

  // Switching lab (or logging in as someone else) invalidates the caches
  useEffect(() => {
    setSkus([]);
    setSkusLoaded(false);
    setTemplates({});
    setTemplatesLoaded(false);
    setBranding(null);
  }, [labId]);

  const value = useMemo(
    () => ({
      toast,
      skus, loadSkus, invalidateSkus: () => setSkusLoaded(false),
      templates, loadTemplates, invalidateTemplates: () => setTemplatesLoaded(false),
      branding, loadBranding, setBranding, defaultLayout,
    }),
    [toast, skus, loadSkus, templates, loadTemplates, branding, loadBranding, defaultLayout]
  );

  return (
    <AppContext.Provider value={value}>
      {children}
      <div className="lab-toasts">
        {toasts.map((t) => (
          <div key={t.id} className={`lab-toast ${t.kind}`}>{t.text}</div>
        ))}
      </div>
    </AppContext.Provider>
  );
};

export default AppProvider;
